/**
 * On or off, as a switch.
 *
 * The third control kind, and the one the contract said would arrive when a knob needed it. A
 * motion the player either sees or does not is not a magnitude and not a short list of named
 * options: half a head bob is still a head bob, and offering "on" and "off" as two buttons in a
 * radio group would draw a choice between two things when the whole content of the setting is
 * whether one thing happens.
 *
 * A native `button` carrying `role="switch"`, so it arrives keyboard reachable, focusable and
 * announced with its state without any of that being written here. `aria-checked` is what carries
 * which way it is thrown, and the class the sheet draws is the same fact for anyone reading the
 * picture instead.
 *
 * Only `onCommit` fires, exactly as `choiceButtons.ts` states for its own: a press is the decision
 * rather than a path towards one, so it applies and writes in the same call.
 *
 * The stored value is `on` or `off` rather than 0 and 1. Both go to disk as they are, and a
 * settings section stays a record anybody can read: `{ headBob: 'off' }` says what the player chose
 * where a zero would need this file to explain it.
 */

import { el, setClass } from '@/ui/dom';
import { SETTING_TOGGLE_OFF, SETTING_TOGGLE_ON } from '@/text/settingsText';
import type {
  SettingControl,
  SettingControlDefinition,
  SettingControlHandle,
  SettingControlOptions,
  SettingValue,
} from '@/ui/settings/settingContract';

/** What goes to disk, and the only two values this control ever produces. */
export const TOGGLE_ON = 'on';
export const TOGGLE_OFF = 'off';

/** A knob's default, stated as the thing it means rather than as one of two strings. */
export function toggleValue(on: boolean): SettingValue {
  return on ? TOGGLE_ON : TOGGLE_OFF;
}

/**
 * A knob's `apply`, narrowed to what this control produces.
 *
 * The boolean is resolved here rather than in every knob, on the same terms `rangeApply` states:
 * the one place that knows this kind produces `on` and `off` is the place that owns the kind.
 */
export function toggleApply(apply: (on: boolean) => void): (value: SettingValue) => void {
  return (value: SettingValue): void => {
    if (value === TOGGLE_ON || value === TOGGLE_OFF) apply(value === TOGGLE_ON);
  };
}

export const TOGGLE_SWITCH: SettingControlDefinition = {
  kind: 'toggle',

  /** One of the two, or nothing. There is no third value to pull an unknown one back towards. */
  coerce(control: SettingControl, raw: unknown): SettingValue | null {
    if (control.kind !== 'toggle') return null;
    if (raw !== TOGGLE_ON && raw !== TOGGLE_OFF) return null;
    return raw;
  },

  create(options: SettingControlOptions): SettingControlHandle {
    const control = options.definition.control;
    // The registry resolved this control by its own kind, so nothing else can arrive here. An
    // empty handle rather than a throw: a settings screen missing one row beats one that is blank.
    if (control.kind !== 'toggle') return { root: el('div', 'settings-control'), dispose: noop };

    const root = el('div', 'settings-control settings-toggle');

    const button = el('button', 'settings-toggleSwitch');
    button.type = 'button';
    button.role = 'switch';
    button.ariaLabel = options.definition.label;

    const state = el('span', 'settings-toggleState');
    let on = options.value === TOGGLE_ON;

    const draw = (): void => {
      setClass(button, 'is-on', on);
      button.ariaChecked = String(on);
      state.textContent = on ? SETTING_TOGGLE_ON : SETTING_TOGGLE_OFF;
    };

    const press = (): void => {
      on = !on;
      draw();
      options.onCommit(toggleValue(on));
    };

    draw();
    button.addEventListener('click', press);
    root.append(button, state);

    return {
      root,
      dispose(): void {
        button.removeEventListener('click', press);
        root.remove();
      },
    };
  },
};

/** What the unreachable branch in `create` hands back, so a missing row disposes like any other. */
function noop(): void {
  return undefined;
}
